import React, { useEffect, useState } from 'react'
import { Users, Tag, Calendar } from 'lucide-react'
import { LeadCheckboxField, LeadFormField, LeadSelectField, LeadTextareaField } from '../form'
import { AutocompleteInput, AutocompleteOption } from '../../ui'
import { apiService } from '../../../services/api'

interface SellerTabProps {
 sellerId?: string
 source?: string
 notes?: string
  contactCount?: number | null
  engagement?: string
  purchaseIntent?: string
  usageType?: string
  vehicleMoment?: string
  hasTradeIn?: boolean
  tradeInVehicleDescription?: string
  tradeInVehicles?: Array<{
    brand: string
    model: string
    version: string
  }>
  tradeInEvaluationEligible?: boolean
  tradeInEvaluationScore?: number | null
  tradeInEstimatedValue?: number | null
  tradeInEvaluationNotes?: string
  wantsFinancing?: boolean
  financingWithTradeIn?: boolean
  downPaymentAmount?: number | null
  downPaymentEntries?: Array<{
    amount: number | null
    note: string
  }>
  desiredInstallmentAmount?: number | null
  approvedCreditAmount?: number | null
  creditScore?: number | null
  financingNotes?: string
 sellers: Array<{ value: string; label: string }>
 sourceOptions: Array<{ value: string; label: string }>
 onSellerChange: (value: string) => void
 onSourceChange: (value: string) => void
 onNotesChange: (value: string) => void
  onFieldChange: (field: string, value: unknown) => void
}

export const SellerTab: React.FC<SellerTabProps> = ({
 sellerId,
 source,
 notes,
  contactCount,
  engagement,
  purchaseIntent,
  usageType,
  vehicleMoment,
  hasTradeIn,
  tradeInVehicleDescription,
  tradeInVehicles = [],
  tradeInEvaluationEligible,
  tradeInEvaluationScore,
  tradeInEstimatedValue,
  tradeInEvaluationNotes,
  wantsFinancing,
  financingWithTradeIn,
  downPaymentAmount,
  downPaymentEntries = [],
  desiredInstallmentAmount,
  approvedCreditAmount,
  creditScore,
  financingNotes,
 sellers,
 sourceOptions,
 onSellerChange,
 onSourceChange,
  onNotesChange,
  onFieldChange
}) => {
 const [brandOptions, setBrandOptions] = useState<AutocompleteOption[]>([])
 const [modelOptionsByRow, setModelOptionsByRow] = useState<Record<number, AutocompleteOption[]>>({})
 const [versionOptionsByRow, setVersionOptionsByRow] = useState<Record<number, AutocompleteOption[]>>({})
 const [brandSearchByRow, setBrandSearchByRow] = useState<Record<number, string>>({})
 const [modelSearchByRow, setModelSearchByRow] = useState<Record<number, string>>({})
 const [versionSearchByRow, setVersionSearchByRow] = useState<Record<number, string>>({})
 const [isCatalogLoading, setIsCatalogLoading] = useState(false)

 useEffect(() => {
  const run = async () => {
    try {
      setIsCatalogLoading(true)
      const brands = await apiService.getBrands('B')
      setBrandOptions(
        brands.map((brand) => ({
          id: brand.id,
          label: brand.name,
          image: brand.logoUrl || undefined,
        }))
      )
    } finally {
      setIsCatalogLoading(false)
    }
  }
  void run()
 }, [])

 const addTradeInVehicle = () => {
   const next = [...tradeInVehicles, { brand: '', model: '', version: '' }]
   onFieldChange('tradeInVehicles', next)
 }

 const updateTradeInVehicle = (index: number, field: 'brand' | 'model' | 'version', value: string) => {
   const next = tradeInVehicles.map((vehicle, currentIndex) =>
     currentIndex === index ? { ...vehicle, [field]: value } : vehicle
   )
   onFieldChange('tradeInVehicles', next)
 }

 const removeTradeInVehicle = (index: number) => {
   const next = tradeInVehicles.filter((_, currentIndex) => currentIndex !== index)
   onFieldChange('tradeInVehicles', next)
 }

 const handleTradeInBrandSelect = async (index: number, option: AutocompleteOption) => {
  updateTradeInVehicle(index, 'brand', option.label)
  updateTradeInVehicle(index, 'model', '')
  updateTradeInVehicle(index, 'version', '')
  setBrandSearchByRow((prev) => ({ ...prev, [index]: option.label }))
  setModelSearchByRow((prev) => ({ ...prev, [index]: '' }))
  setVersionSearchByRow((prev) => ({ ...prev, [index]: '' }))
  setVersionOptionsByRow((prev) => ({ ...prev, [index]: [] }))
  if (!option.id) {
    setModelOptionsByRow((prev) => ({ ...prev, [index]: [] }))
    return
  }
  const models = await apiService.getBrandModels(option.id)
  setModelOptionsByRow((prev) => ({
    ...prev,
    [index]: models.map((model) => ({ id: model.id, label: model.name })),
  }))
 }

 const handleTradeInModelSelect = async (index: number, option: AutocompleteOption) => {
  updateTradeInVehicle(index, 'model', option.label)
  updateTradeInVehicle(index, 'version', '')
  setModelSearchByRow((prev) => ({ ...prev, [index]: option.label }))
  setVersionSearchByRow((prev) => ({ ...prev, [index]: '' }))
  if (!option.id) {
    setVersionOptionsByRow((prev) => ({ ...prev, [index]: [] }))
    return
  }
  const versions = await apiService.getModelVersions(option.id)
  setVersionOptionsByRow((prev) => ({
    ...prev,
    [index]: versions.map((version) => ({ id: version.id, label: version.name })),
  }))
 }

 const handleTradeInVersionSelect = (index: number, option: AutocompleteOption) => {
  updateTradeInVehicle(index, 'version', option.label)
  setVersionSearchByRow((prev) => ({ ...prev, [index]: option.label }))
 }

 const addDownPaymentEntry = () => {
   const next = [...downPaymentEntries, { amount: null, note: '' }]
   onFieldChange('downPaymentEntries', next)
 }

 const updateDownPaymentEntry = (index: number, field: 'amount' | 'note', value: number | string | null) => {
   const next = downPaymentEntries.map((entry, currentIndex) =>
     currentIndex === index ? { ...entry, [field]: value } : entry
   )
   onFieldChange('downPaymentEntries', next)
 }

 const removeDownPaymentEntry = (index: number) => {
   const next = downPaymentEntries.filter((_, currentIndex) => currentIndex !== index)
   onFieldChange('downPaymentEntries', next)
 }

 return (
 <div className="space-y-6">
 {/* Atribuição */}
 <div className="space-y-4">
 <div className="flex items-center gap-2 border-b border-border pb-2">
 <Users className="h-4 w-4 text-accent" />
 <h3 className="text-sm font-semibold text-foreground">
 Atribuição
 </h3>
 </div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  <LeadFormField
    label="Contatos Realizados"
    type="number"
    value={contactCount?.toString() || ''}
    onChange={(e) => onFieldChange('contactCount', e.target.value ? Number(e.target.value) : null)}
  />
  <LeadSelectField
    label="Engajamento"
    value={engagement || ''}
    options={[
      { value: '', label: 'Selecione...' },
      { value: 'cold', label: 'Frio' },
      { value: 'warm', label: 'Morno' },
      { value: 'hot', label: 'Quente' },
    ]}
    onChange={(value) => onFieldChange('engagement', value)}
  />
  <LeadSelectField
    label="Intenção de Compra"
    value={purchaseIntent || ''}
    options={[
      { value: '', label: 'Selecione...' },
      { value: 'urgent', label: 'Urgente' },
      { value: 'researching', label: 'Pesquisando' },
      { value: 'comparing', label: 'Comparando' },
      { value: 'undecided', label: 'Indeciso' },
    ]}
    onChange={(value) => onFieldChange('purchaseIntent', value)}
  />
  <LeadSelectField
    label="Tipo de Uso"
    value={usageType || ''}
    options={[
      { value: '', label: 'Selecione...' },
      { value: 'personal', label: 'Pessoal' },
      { value: 'uber', label: 'Uber/App' },
      { value: 'commercial', label: 'Comercial' },
      { value: 'family', label: 'Família' },
      { value: 'work', label: 'Trabalho' },
      { value: 'other', label: 'Outro' },
    ]}
    onChange={(value) => onFieldChange('usageType', value)}
  />
  <LeadSelectField
    label="Momento"
    value={vehicleMoment || ''}
    options={[
      { value: '', label: 'Selecione...' },
      { value: 'first_car', label: 'Primeiro carro' },
      { value: 'upgrade', label: 'Upgrade' },
      { value: 'replacement', label: 'Substituição' },
      { value: 'additional', label: 'Carro adicional' },
    ]}
    onChange={(value) => onFieldChange('vehicleMoment', value)}
  />
</div>

<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  <LeadCheckboxField
    checked={Boolean(hasTradeIn)}
    onCheckedChange={(checked) => onFieldChange('hasTradeIn', checked)}
    label="Tem veículo na troca"
  />
  <LeadCheckboxField
    checked={Boolean(wantsFinancing)}
    onCheckedChange={(checked) => onFieldChange('wantsFinancing', checked)}
    label="Deseja financiamento"
  />
  <LeadCheckboxField
    checked={Boolean(financingWithTradeIn)}
    onCheckedChange={(checked) => onFieldChange('financingWithTradeIn', checked)}
    label="Troca + financiamento"
  />
  <LeadCheckboxField
    checked={Boolean(tradeInEvaluationEligible)}
    onCheckedChange={(checked) => onFieldChange('tradeInEvaluationEligible', checked)}
    label="Troca elegível nas regras"
  />
</div>

<LeadFormField
  label="Qual veículo para troca?"
  value={tradeInVehicleDescription || ''}
  onChange={(e) => onFieldChange('tradeInVehicleDescription', e.target.value)}
/>
<div className="space-y-2">
  <div className="flex items-center justify-between">
    <p className="text-sm font-medium text-foreground">Veículos na troca</p>
    <button
      type="button"
      onClick={addTradeInVehicle}
      className="px-2 py-1 text-xs rounded-md border border-border hover:bg-muted"
    >
      + Adicionar veículo
    </button>
  </div>
  {tradeInVehicles.length === 0 && (
    <p className="text-xs text-muted-foreground">Nenhum veículo de troca adicionado.</p>
  )}
  {tradeInVehicles.map((vehicle, index) => (
    <div key={`tradein-${index}`} className="grid grid-cols-1 md:grid-cols-3 gap-2">
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Marca</p>
        <AutocompleteInput
          value={brandSearchByRow[index] ?? vehicle.brand}
          options={brandOptions}
          selectedId={brandOptions.find((brand) => brand.label === vehicle.brand)?.id || ''}
          onChange={(value) => {
            setBrandSearchByRow((prev) => ({ ...prev, [index]: value }))
            updateTradeInVehicle(index, 'brand', value)
          }}
          onSelect={(option) => void handleTradeInBrandSelect(index, option)}
          placeholder="Buscar marca..."
          loading={isCatalogLoading}
          emptyMessage="Nenhuma marca encontrada"
        />
      </div>
      <div>
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Modelo</p>
        <AutocompleteInput
          value={modelSearchByRow[index] ?? vehicle.model}
          options={modelOptionsByRow[index] || []}
          selectedId={(modelOptionsByRow[index] || []).find((model) => model.label === vehicle.model)?.id || ''}
          onChange={(value) => {
            setModelSearchByRow((prev) => ({ ...prev, [index]: value }))
            updateTradeInVehicle(index, 'model', value)
          }}
          onSelect={(option) => void handleTradeInModelSelect(index, option)}
          placeholder={vehicle.brand ? 'Buscar modelo...' : 'Selecione uma marca primeiro'}
          disabled={!vehicle.brand}
          emptyMessage="Nenhum modelo encontrado"
        />
      </div>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">Versão</p>
          <AutocompleteInput
            value={versionSearchByRow[index] ?? vehicle.version}
            options={versionOptionsByRow[index] || []}
            selectedId={(versionOptionsByRow[index] || []).find((version) => version.label === vehicle.version)?.id || ''}
            onChange={(value) => {
              setVersionSearchByRow((prev) => ({ ...prev, [index]: value }))
              updateTradeInVehicle(index, 'version', value)
            }}
            onSelect={(option) => handleTradeInVersionSelect(index, option)}
            placeholder={vehicle.model ? 'Buscar versão...' : 'Selecione um modelo primeiro'}
            disabled={!vehicle.model}
            emptyMessage="Nenhuma versão encontrada"
          />
        </div>
        <button
          type="button"
          onClick={() => removeTradeInVehicle(index)}
          className="mb-0.5 px-2 py-2 text-xs rounded-md border border-border hover:bg-muted"
        >
          Remover
        </button>
      </div>
    </div>
  ))}
</div>

<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
  <LeadFormField
    label="Score Avaliação"
    type="number"
    value={tradeInEvaluationScore?.toString() || ''}
    onChange={(e) => onFieldChange('tradeInEvaluationScore', e.target.value ? Number(e.target.value) : null)}
  />
  <LeadFormField
    label="Valor Estimado Troca"
    type="number"
    value={tradeInEstimatedValue?.toString() || ''}
    onChange={(e) => onFieldChange('tradeInEstimatedValue', e.target.value ? Number(e.target.value) : null)}
  />
  <LeadFormField
    label="Score Crédito"
    type="number"
    value={creditScore?.toString() || ''}
    onChange={(e) => onFieldChange('creditScore', e.target.value ? Number(e.target.value) : null)}
  />
</div>

<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
  <LeadFormField
    label="Entrada"
    type="number"
    value={downPaymentAmount?.toString() || ''}
    onChange={(e) => onFieldChange('downPaymentAmount', e.target.value ? Number(e.target.value) : null)}
  />
  <LeadFormField
    label="Parcela Desejada"
    type="number"
    value={desiredInstallmentAmount?.toString() || ''}
    onChange={(e) => onFieldChange('desiredInstallmentAmount', e.target.value ? Number(e.target.value) : null)}
  />
  <LeadFormField
    label="Crédito Aprovado"
    type="number"
    value={approvedCreditAmount?.toString() || ''}
    onChange={(e) => onFieldChange('approvedCreditAmount', e.target.value ? Number(e.target.value) : null)}
  />
</div>
<div className="space-y-2">
  <div className="flex items-center justify-between">
    <p className="text-sm font-medium text-foreground">Entradas</p>
    <button
      type="button"
      onClick={addDownPaymentEntry}
      className="px-2 py-1 text-xs rounded-md border border-border hover:bg-muted"
    >
      + Adicionar entrada
    </button>
  </div>
  {downPaymentEntries.length === 0 && (
    <p className="text-xs text-muted-foreground">Nenhuma entrada adicionada.</p>
  )}
  {downPaymentEntries.map((entry, index) => (
    <div key={`entry-${index}`} className="grid grid-cols-1 md:grid-cols-3 gap-2">
      <LeadFormField
        label="Valor da entrada"
        type="number"
        value={entry.amount?.toString() || ''}
        onChange={(e) => updateDownPaymentEntry(index, 'amount', e.target.value ? Number(e.target.value) : null)}
      />
      <LeadFormField
        label="Descrição"
        value={entry.note}
        onChange={(e) => updateDownPaymentEntry(index, 'note', e.target.value)}
      />
      <div className="flex items-end">
        <button
          type="button"
          onClick={() => removeDownPaymentEntry(index)}
          className="w-full mb-0.5 px-2 py-2 text-xs rounded-md border border-border hover:bg-muted"
        >
          Remover
        </button>
      </div>
    </div>
  ))}
</div>

<LeadTextareaField
  value={tradeInEvaluationNotes || ''}
  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onFieldChange('tradeInEvaluationNotes', e.target.value)}
  placeholder="Detalhes da avaliação da troca"
  rows={3}
/>

<LeadTextareaField
  value={financingNotes || ''}
  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onFieldChange('financingNotes', e.target.value)}
  placeholder="Detalhes de financiamento"
  rows={3}
/>
 <LeadSelectField
 label="Vendedor Responsável"
 value={sellerId || ''}
 options={[
 { value: '', label: 'Selecione um vendedor...' },
 ...sellers
 ]}
 onChange={onSellerChange}
 />
 </div>

 {/* Origem */}
 <div className="space-y-4">
 <div className="flex items-center gap-2 border-b border-border pb-2">
 <Tag className="h-4 w-4 text-accent" />
 <h3 className="text-sm font-semibold text-foreground">
 Origem do Lead
 </h3>
 </div>
 <LeadSelectField
 label="Origem"
 value={source || ''}
 options={[
 { value: '', label: 'Selecione a origem...' },
 ...sourceOptions
 ]}
 onChange={onSourceChange}
 />
 </div>

 {/* Observações */}
 <div className="space-y-4">
 <div className="flex items-center gap-2 border-b border-border pb-2">
 <Calendar className="h-4 w-4 text-accent" />
 <h3 className="text-sm font-semibold text-foreground">
 Observações
 </h3>
 </div>
 <LeadTextareaField
 value={notes || ''}
 onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onNotesChange(e.target.value)}
 placeholder="Adicione observações sobre o atendimento, preferências do cliente, próximos passos, etc."
 rows={4}
 />
 </div>
 </div>
 )
}
